// @ts-nocheck
import { ArrowLeft } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link,useNavigate, useParams } from "react-router-dom";

import DiarioFormSections from "@/features/diario/components/DiarioFormSections";
import LayoutRelatorio from "@/features/diario/components/LayoutRelatorio";
import { getRelatorioById } from "@/features/diario/services/relatoriosService";
import DocumentGallery from "@/features/midia/components/DocumentGallery";
import VideoGallery from "@/features/midia/components/VideoGallery";
import { useRouteRefresh } from "@/features/sistema/hooks/useRouteRefresh";
import { supabase } from "@/features/sistema/services/supabaseClient";

export default function ClienteDiario() {
  const { obraId, relatorioId } = useParams();
  const navigate = useNavigate();
  
  const [relatorio, setRelatorio] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [fotos, setFotos] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [anexos, setAnexos] = useState<any[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  useRouteRefresh(() => {
    if (!relatorioId) return;
    (async () => {
      setLoading(true);
      const r = await getRelatorioById(relatorioId);
      setRelatorio(r);
      // 🔎 Buscar mídias do relatório
      const [f, v, a] = await Promise.all([
        supabase.from("fotos").select("id,url,nome,relatorio_id").eq("relatorio_id", relatorioId),
        supabase.from("videos").select("id,url,nome,relatorio_id").eq("relatorio_id", relatorioId),
        supabase.from("anexos").select("id,url,nome,relatorio_id").eq("relatorio_id", relatorioId)  // se não existir, remova esta linha
      ]);
      setFotos(Array.isArray(f.data) ? f.data.map(x => ({ ...x, tipo: "foto" })) : []);
      setVideos(Array.isArray(v.data) ? v.data.map(x => ({ ...x, tipo: "video" })) : []);
      setAnexos(Array.isArray(a?.data) ? a.data.map(x => ({ ...x, tipo: "anexo" })) : []);
      setLoading(false);
    })();
  });

  if (loading) return <div className="p-4">Carregando…</div>;
  if (!relatorio) return <div className="p-4">Relatório não encontrado.</div>;

  const hasFotos = fotos.length > 0;
  const hasVideos = videos.length > 0;
  const hasAnexos = anexos.length > 0;

  // 🔧 Monta o "form" no mesmo shape do editor — só que iremos renderizar em readOnly
  const form = {
    numero: relatorio.numero,
    data: relatorio.data,                  // já vem em YYYY-MM-DD do seu serviço
    status: relatorio.status || "aprovado",
    clima_manha: relatorio.clima_manha ?? null,
    clima_tarde: relatorio.clima_tarde ?? null,
    clima_noite: relatorio.clima_noite ?? null,
    mao_obra: Array.isArray(relatorio.mao_obra) ? relatorio.mao_obra : [],
    equipamentos: Array.isArray(relatorio.equipamentos) ? relatorio.equipamentos : [],
    atividades: Array.isArray(relatorio.atividades) ? relatorio.atividades : [],
    ocorrencias: Array.isArray(relatorio.ocorrencias) ? relatorio.ocorrencias : [],
    comentarios: relatorio.comentarios ?? "",
    anexos: [...fotos, ...videos, ...anexos], // só para espelhar contadores/indicadores visuais
    alteracoes_projeto: relatorio.alteracoes_projeto ?? { texto: "" }
  };

  // No-ops porque estamos em readOnly
  const noop = () => {};
  const noopIdx = (_i:number,_n:number) => {};
  const openGallery = (_idx:number) => {};
  const modoEscuro = false;
  const saving = false;
  const uploading = false;

  const handleLogout = () => {
    // Limpar dados de autenticação se necessário
    localStorage.removeItem('authToken');
    sessionStorage.clear();
    
    // Redirecionar para login
    navigate('/login');
  };

  return (
    <LayoutRelatorio>
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto space-y-4 p-4">
        <div className="flex items-center gap-2">
          <Link to={`/cliente/obras/${obraId}`} className="text-primary hover:opacity-80">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-ageo-bold text-foreground">Diário #{relatorio.numero}</h1>
        </div>

        {/* 👇 mesmo layout de edição, mas travado */}
        <DiarioFormSections
          form={form}
          updateField={noop}
          updateQtdMao={noopIdx}
          removeMao={noopIdx as any}
          updateQtdEquip={noopIdx}
          removeEquip={noopIdx as any}
          handleFileUpload={noop as any}
          removeAnexo={noop as any}
          mediaItems={form.anexos}
          openGallery={openGallery}
          modoEscuro={modoEscuro}
          saving={saving}
          uploading={uploading}
          setShowMaoModal={noop}
          setShowEquipModal={noop}
          readOnly
          enableSelfEditOnReadOnly
          showMedia={false}  // ⛔️ esconde upload/galeria interna para não duplicar
        />


        {/* Galerias (somente leitura) com guards e mensagens */}
        <section className="rounded-xl border border-secondary bg-white p-4 shadow-sm">
          <h3 className="font-ageo-bold mb-2 text-foreground">Fotos</h3>
          {!hasFotos ? (
            <div className="text-sm text-gray-500">Sem fotos neste diário.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1">
              {fotos.map((f:any, idx:number) => (
                <button
                  key={f.id}
                  className="relative group"
                  onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                  title="Visualizar"
                >
                  <img
                    src={f.url}
                    alt="Foto da obra"
                    className="w-full h-24 object-cover rounded-lg shadow-sm hover:opacity-90 transition select-none"
                    loading="lazy"
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-secondary bg-white p-4 shadow-sm">
          <h3 className="font-ageo-bold mb-2 text-foreground">Vídeos</h3>
          {hasVideos ? (
            <VideoGallery readOnly relatorioId={relatorioId!} videos={videos} />
          ) : (
            <div className="text-sm text-gray-500">Sem vídeos neste diário.</div>
          )}
        </section>

        <section className="rounded-xl border border-secondary bg-white p-4 shadow-sm">
          <h3 className="font-ageo-bold mb-2 text-foreground">Documentos</h3>
          {hasAnexos ? (
            <DocumentGallery readOnly relatorioId={relatorioId!} anexos={anexos} documentos={anexos} files={anexos} />
          ) : (
            <div className="text-sm text-gray-500">Sem documentos neste diário.</div>
          )}
        </section>

        {/* Lightbox de fotos */}
        {lightboxOpen && hasFotos && fotos[lightboxIndex] && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            role="dialog"
            aria-modal="true"
            onWheel={(e) => {
              if (e.deltaY > 0) setLightboxIndex(i => Math.min(fotos.length - 1, i + 1));
              if (e.deltaY < 0) setLightboxIndex(i => Math.max(0, i - 1));
            }}
            onTouchStart={(e) => setTouchStartX(e.changedTouches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchStartX === null) return;
              const dx = e.changedTouches[0].clientX - touchStartX;
              if (dx > 40) setLightboxIndex(i => Math.max(0, i - 1));
              if (dx < -40) setLightboxIndex(i => Math.min(fotos.length - 1, i + 1));
              setTouchStartX(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") setLightboxIndex(i => Math.max(0, i - 1));
              if (e.key === "ArrowRight") setLightboxIndex(i => Math.min(fotos.length - 1, i + 1));
              if (e.key === "Escape") setLightboxOpen(false);
            }}
            tabIndex={-1}
          >
            <button
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => setLightboxOpen(false)}
              title="Fechar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 text-white"><path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 1 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.9a1 1 0 0 0 1.41-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>
            </button>
            <button
              className="absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => setLightboxIndex(i => Math.max(0, i - 1))}
              title="Anterior"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-8 h-8 text-white"><path fill="currentColor" d="M15.41 7.41L14 6l-6 6l6 6l1.41-1.41L10.83 12z"/></svg>
            </button>
            <img
              src={fotos[lightboxIndex].url}
              alt="Foto da obra"
              className="max-h-[80vh] max-w-[90vw] rounded-lg shadow-2xl select-none"
              draggable={false}
            />
            <button
              className="absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => setLightboxIndex(i => Math.min(fotos.length - 1, i + 1))}
              title="Próxima"
              style={{ top: '50%' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-8 h-8 text-white"><path fill="currentColor" d="m8.59 16.59L13.17 12L8.59 7.41L10 6l6 6l-6 6z"/></svg>
            </button>
          </div>
        )}
      </div>
    </div>
    </LayoutRelatorio>
  );
}
